import api from '../../lib/api';
import type {
  CompetenceRecord,
  GapSummary,
  JobRole,
  JobRolePayload,
  RecordPayload,
  TrainingPayload,
  TrainingSession,
  TrainingStatus,
  TrainingType,
} from './types';

export const fetchJobRoles = async (params: { active?: boolean } = {}): Promise<JobRole[]> => {
  const { data } = await api.get<{ data: JobRole[] }>('competence/job-roles', { params });
  return data.data;
};

export const createJobRole = async (payload: JobRolePayload): Promise<JobRole> => {
  const { data } = await api.post<JobRole>('competence/job-roles', payload);
  return data;
};

export const updateJobRole = async (id: string, payload: Partial<JobRolePayload>): Promise<JobRole> => {
  const { data } = await api.put<JobRole>(`competence/job-roles/${id}`, payload);
  return data;
};

export const fetchCompetenceRecords = async (params: {
  jobRoleId?: string;
  userId?: number;
} = {}): Promise<CompetenceRecord[]> => {
  const { data } = await api.get<{ data: CompetenceRecord[] }>('competence/records', { params });
  return data.data;
};

export const createCompetenceRecord = async (payload: RecordPayload): Promise<CompetenceRecord> => {
  const { data } = await api.post<CompetenceRecord>('competence/records', payload);
  return data;
};

export const updateCompetenceRecord = async (
  id: string,
  payload: Partial<RecordPayload>,
): Promise<CompetenceRecord> => {
  const { data } = await api.put<CompetenceRecord>(`competence/records/${id}`, payload);
  return data;
};

export const fetchCompetenceGaps = async (): Promise<GapSummary[]> => {
  const { data } = await api.get<{ data: GapSummary[] }>('competence/records/gaps');
  return data.data;
};

export const fetchTrainings = async (params: {
  status?: TrainingStatus;
  type?: TrainingType;
} = {}): Promise<TrainingSession[]> => {
  const { data } = await api.get<{ data: TrainingSession[] }>('competence/trainings', { params });
  return data.data;
};

export const fetchTraining = async (id: string): Promise<TrainingSession> => {
  const { data } = await api.get<TrainingSession>(`competence/trainings/${id}`);
  return data;
};

export const createTraining = async (payload: TrainingPayload): Promise<TrainingSession> => {
  const { data } = await api.post<TrainingSession>('competence/trainings', payload);
  return data;
};

export const updateTraining = async (
  id: string,
  payload: Partial<TrainingPayload>,
): Promise<TrainingSession> => {
  const { data } = await api.put<TrainingSession>(`competence/trainings/${id}`, payload);
  return data;
};

export const addTrainingAttendees = async (
  sessionId: string,
  attendees: Array<{ userId?: number | null; externalPersonName?: string | null }>,
) => {
  const { data } = await api.post(`competence/trainings/${sessionId}/attendees`, { attendees });
  return data.data;
};

export const patchTrainingAttendee = async (
  attendeeId: string,
  payload: { attended?: boolean; score?: number | null; passed?: boolean | null },
) => {
  const { data } = await api.patch(`competence/attendees/${attendeeId}`, payload);
  return data;
};

export const recordTrainingEffectiveness = async (
  sessionId: string,
  effectivenessResult: string,
): Promise<TrainingSession> => {
  const { data } = await api.post<TrainingSession>(`competence/trainings/${sessionId}/effectiveness`, {
    effectivenessResult,
  });
  return data;
};
